import React from 'react';
import { Link } from 'react-router-dom';

const MemorialStyles: React.FC = () => {
  return (
    <div className="relative">
      {/* Hero Section */}
      <div className="pt-48 pb-16 md:pt-48 md:pb-20">
        <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-center mb-6">
          <span className="text-white">Choose Your</span>
          <span className="block mt-4 pb-2 bg-gradient-to-r from-primary to-[#E6C45C] text-transparent bg-clip-text">
            Memorial Style
          </span>
        </h1>
        <p className="text-[#FFFFFFCC] text-lg max-w-2xl mx-auto text-center">
          Each style has been thoughtfully crafted to honor your loved one's memory in a unique and meaningful way
        </p>
      </div>

      {/* Classic Elegance Style */}
      <div className="py-20 md:py-32 relative group">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse" 
               style={{ animationDuration: '4s' }} />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#E6C45C]/5 rounded-full blur-3xl animate-pulse"
               style={{ animationDuration: '6s' }} />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            {/* Content */}
            <div className="flex-1 relative">
              {/* Decorative Line */}
              <div className="absolute -left-8 top-0 w-px h-full bg-gradient-to-b from-primary/0 via-primary/20 to-primary/0 group-hover:h-[120%] transition-all duration-700" />
              
              <div className="text-primary uppercase tracking-wider font-medium mb-4 transform group-hover:-translate-x-2 transition-transform duration-500">
                TIMELESS & SOPHISTICATED
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-montserrat group-hover:text-primary transition-colors duration-500">
                Classic Elegance
              </h2>
              <p className="text-[#FFFFFFCC] text-lg leading-relaxed mb-8 transform group-hover:translate-x-2 transition-transform duration-500">
                A dignified and timeless design that pays tribute with grace and sophistication. Perfect for those who appreciate traditional aesthetics with a modern touch.
              </p>
              
              {/* Features List */}
              <div className="space-y-4 mb-8">
                {['Elegant typography and rich accents', 'Formal layout with refined spacing', 'Sophisticated photo presentations'].map((feature, index) => (
                  <div key={index} 
                       className="flex items-center gap-3 transform translate-y-0 transition-transform duration-500"
                       style={{ transitionDelay: `${index * 100}ms` }}>
                    <div className="w-2 h-2 rounded-full bg-primary transform group-hover:scale-150 transition-transform duration-500" />
                    <span className="text-white group-hover:text-primary/90 transition-colors duration-500">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="text-[#FFFFFFCC] mb-8 transform group-hover:translate-x-2 transition-transform duration-500">
                <span className="text-white font-semibold">Best suited for:</span> Those seeking a traditional memorial with timeless elegance, perfect for professional individuals and those who appreciated life's refined moments.
              </div>

              <a 
                href="https://classic-elegance.digitalmemorials.ie/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:text-white transition-all duration-300 group/button"
              >
                <span className="font-montserrat relative">
                  View Demo
                  <span className="absolute inset-x-0 -bottom-1 h-px bg-primary scale-x-0 group-hover/button:scale-x-100 transition-transform duration-500" />
                </span>
                <svg className="w-5 h-5 transform group-hover/button:translate-x-1 transition-transform duration-300" 
                     fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            {/* Image */}
            <div className="flex-1">
              <div className="relative transform group-hover:scale-[1.02] transition-transform duration-700">
                <div className="aspect-[16/10] rounded-2xl overflow-hidden">
                  <img
                    src="/images/CE-Laptop.svg"
                    alt="Classic Elegance Memorial Style"
                    className="w-full h-full object-contain scale-110"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Minimalist Style */}
      <div className="py-20 md:py-32 relative group">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse" 
               style={{ animationDuration: '5s' }} />
          <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#E6C45C]/5 rounded-full blur-3xl animate-pulse"
               style={{ animationDuration: '7s' }} />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-16">
            {/* Content */}
            <div className="flex-1 relative">
              {/* Decorative Line */}
              <div className="absolute -right-8 top-0 w-px h-full bg-gradient-to-b from-primary/0 via-primary/20 to-primary/0 group-hover:h-[120%] transition-all duration-700" />
              
              <div className="text-primary uppercase tracking-wider font-medium mb-4 transform group-hover:translate-x-2 transition-transform duration-500">
                CLEAN & CONTEMPORARY
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-montserrat group-hover:text-primary transition-colors duration-500">
                Modern Minimalist
              </h2>
              <p className="text-[#FFFFFFCC] text-lg leading-relaxed mb-8 transform group-hover:-translate-x-2 transition-transform duration-500">
                A contemporary design that focuses on simplicity and clean aesthetics. Ideal for those who appreciate modern design and minimalist beauty.
              </p>
              
              {/* Features List with Staggered Animation */}
              <div className="space-y-4 mb-8">
                {['Clean lines and minimal elements', 'Focus on visual content', 'Sleek, intuitive navigation'].map((feature, index) => (
                  <div key={index} 
                       className="flex items-center gap-3 transform translate-y-0 transition-transform duration-500"
                       style={{ transitionDelay: `${index * 100}ms` }}>
                    <div className="w-2 h-2 rounded-full bg-primary transform group-hover:scale-150 transition-transform duration-500" />
                    <span className="text-white group-hover:text-primary/90 transition-colors duration-500">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="text-[#FFFFFFCC] mb-8 transform group-hover:-translate-x-2 transition-transform duration-500">
                <span className="text-white font-semibold">Best suited for:</span> Those who appreciate modern aesthetics and clean design, perfect for individuals who lived with purpose and elegant simplicity.
              </div>

              <a 
                href="https://modern-minimalist.digitalmemorials.ie/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:text-white transition-all duration-300 group/button"
              >
                <span className="font-montserrat relative">
                  View Demo
                  <span className="absolute inset-x-0 -bottom-1 h-px bg-primary scale-x-0 group-hover/button:scale-x-100 transition-transform duration-500" />
                </span>
                <svg className="w-5 h-5 transform group-hover/button:translate-x-1 transition-transform duration-300" 
                     fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            {/* Image */}
            <div className="flex-1">
              <div className="relative transform group-hover:scale-[1.02] transition-transform duration-700">
                <div className="aspect-[16/10] rounded-2xl overflow-hidden">
                  <img
                    src="/images/MM-Laptop.svg"
                    alt="Modern Minimalist Memorial Style"
                    className="w-full h-full object-contain scale-110"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Traditional Ornate Style */}
      <div className="py-20 md:py-32 relative group">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <div className="absolute top-0 left-1/3 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse" 
               style={{ animationDuration: '6s' }} />
          <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-[#E6C45C]/5 rounded-full blur-3xl animate-pulse"
               style={{ animationDuration: '8s' }} />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            {/* Content */}
            <div className="flex-1 relative">
              {/* Decorative Line */}
              <div className="absolute -left-8 top-0 w-px h-full bg-gradient-to-b from-primary/0 via-primary/20 to-primary/0 group-hover:h-[120%] transition-all duration-700" />
              
              <div className="text-primary uppercase tracking-wider font-medium mb-4 transform group-hover:-translate-x-2 transition-transform duration-500">
                CLASSIC & ORNATE
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-montserrat group-hover:text-primary transition-colors duration-500">
                Traditional Ornate
              </h2>
              <p className="text-[#FFFFFFCC] text-lg leading-relaxed mb-8 transform group-hover:translate-x-2 transition-transform duration-500">
                Rich in traditional elements and ornate details, this style honors cherished memories with classic memorial aesthetics.
              </p>
              
              {/* Features List */}
              <div className="space-y-4 mb-8">
                {['Ornate decorative elements', 'Traditional styling', 'Formal presentation'].map((feature, index) => (
                  <div key={index} 
                       className="flex items-center gap-3 transform translate-y-0 transition-transform duration-500"
                       style={{ transitionDelay: `${index * 100}ms` }}>
                    <div className="w-2 h-2 rounded-full bg-primary transform group-hover:scale-150 transition-transform duration-500" />
                    <span className="text-white group-hover:text-primary/90 transition-colors duration-500">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="text-[#FFFFFFCC] mb-8 transform group-hover:translate-x-2 transition-transform duration-500">
                <span className="text-white font-semibold">Best suited for:</span> Those who appreciate traditional values and classic memorial aesthetics with ornate details.
              </div>

              <a 
                href="https://www.traditional-ornate.digitalmemorials.ie/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:text-white transition-all duration-300 group/button"
              >
                <span className="font-montserrat relative">
                  View Demo
                  <span className="absolute inset-x-0 -bottom-1 h-px bg-primary scale-x-0 group-hover/button:scale-x-100 transition-transform duration-500" />
                </span>
                <svg className="w-5 h-5 transform group-hover/button:translate-x-1 transition-transform duration-300" 
                     fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            {/* Image */}
            <div className="flex-1">
              <div className="relative transform group-hover:scale-[1.02] transition-transform duration-700">
                <div className="aspect-[16/10] rounded-2xl overflow-hidden">
                  <img
                    src="/images/TO-Laptop.svg"
                    alt="Traditional Ornate Memorial Style"
                    className="w-full h-full object-contain scale-110"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contemporary Bold Style */}
      <div className="py-20 md:py-32 relative group">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse" 
               style={{ animationDuration: '6s' }} />
          <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#E6C45C]/5 rounded-full blur-3xl animate-pulse"
               style={{ animationDuration: '8s' }} />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-16">
            {/* Content */}
            <div className="flex-1 relative">
              {/* Decorative Line */}
              <div className="absolute -right-8 top-0 w-px h-full bg-gradient-to-b from-primary/0 via-primary/20 to-primary/0 group-hover:h-[120%] transition-all duration-700" />
              
              <div className="text-primary uppercase tracking-wider font-medium mb-4 transform group-hover:translate-x-2 transition-transform duration-500">
                BOLD & MODERN
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-montserrat group-hover:text-primary transition-colors duration-500">
                Contemporary Bold
              </h2>
              <p className="text-[#FFFFFFCC] text-lg leading-relaxed mb-8 transform group-hover:-translate-x-2 transition-transform duration-500">
                A bold and modern approach that makes a statement while maintaining respectful commemoration.
              </p>
              
              {/* Features List */}
              <div className="space-y-4 mb-8">
                {['Bold design elements', 'Modern aesthetics', 'Dynamic layout'].map((feature, index) => (
                  <div key={index} 
                       className="flex items-center gap-3 transform translate-y-0 transition-transform duration-500"
                       style={{ transitionDelay: `${index * 100}ms` }}>
                    <div className="w-2 h-2 rounded-full bg-primary transform group-hover:scale-150 transition-transform duration-500" />
                    <span className="text-white group-hover:text-primary/90 transition-colors duration-500">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="text-[#FFFFFFCC] mb-8 transform group-hover:-translate-x-2 transition-transform duration-500">
                <span className="text-white font-semibold">Best suited for:</span> Those who want to make a bold statement while maintaining the dignity of remembrance.
              </div>

              <a 
                href="https://contemporary-bold.digitalmemorials.ie/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:text-white transition-all duration-300 group/button"
              >
                <span className="font-montserrat relative">
                  View Demo
                  <span className="absolute inset-x-0 -bottom-1 h-px bg-primary scale-x-0 group-hover/button:scale-x-100 transition-transform duration-500" />
                </span>
                <svg className="w-5 h-5 transform group-hover/button:translate-x-1 transition-transform duration-300" 
                     fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            {/* Image */}
            <div className="flex-1">
              <div className="relative transform group-hover:scale-[1.02] transition-transform duration-700">
                <div className="aspect-[16/10] rounded-2xl overflow-hidden">
                  <img
                    src="/images/CB-Laptop.svg"
                    alt="Contemporary Bold Memorial Style"
                    className="w-full h-full object-contain scale-110"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Serene Natural Style */}
      <div className="py-20 md:py-32 relative group">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <div className="absolute top-0 left-1/3 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse" 
               style={{ animationDuration: '7s' }} />
          <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-[#E6C45C]/5 rounded-full blur-3xl animate-pulse"
               style={{ animationDuration: '9s' }} />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            {/* Content */}
            <div className="flex-1 relative">
              {/* Decorative Line */}
              <div className="absolute -left-8 top-0 w-px h-full bg-gradient-to-b from-primary/0 via-primary/20 to-primary/0 group-hover:h-[120%] transition-all duration-700" />
              
              <div className="text-primary uppercase tracking-wider font-medium mb-4 transform group-hover:-translate-x-2 transition-transform duration-500">
                PEACEFUL & NATURAL
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-montserrat group-hover:text-primary transition-colors duration-500">
                Serene Natural
              </h2>
              <p className="text-[#FFFFFFCC] text-lg leading-relaxed mb-8 transform group-hover:translate-x-2 transition-transform duration-500">
                A peaceful design incorporating natural elements and calming aesthetics for a tranquil memorial experience.
              </p>
              
              {/* Features List */}
              <div className="space-y-4 mb-8">
                {['Natural elements', 'Serene aesthetics', 'Organic patterns'].map((feature, index) => (
                  <div key={index} 
                       className="flex items-center gap-3 transform translate-y-0 transition-transform duration-500"
                       style={{ transitionDelay: `${index * 100}ms` }}>
                    <div className="w-2 h-2 rounded-full bg-primary transform group-hover:scale-150 transition-transform duration-500" />
                    <span className="text-white group-hover:text-primary/90 transition-colors duration-500">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="text-[#FFFFFFCC] mb-8 transform group-hover:translate-x-2 transition-transform duration-500">
                <span className="text-white font-semibold">Best suited for:</span> Those who found peace in nature and wish to create a calming space for remembrance.
              </div>

              <a 
                href="https://serene-natural.digitalmemorials.ie/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:text-white transition-all duration-300 group/button"
              >
                <span className="font-montserrat relative">
                  View Demo
                  <span className="absolute inset-x-0 -bottom-1 h-px bg-primary scale-x-0 group-hover/button:scale-x-100 transition-transform duration-500" />
                </span>
                <svg className="w-5 h-5 transform group-hover/button:translate-x-1 transition-transform duration-300" 
                     fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            {/* Image */}
            <div className="flex-1">
              <div className="relative transform group-hover:scale-[1.02] transition-transform duration-700">
                <div className="aspect-[16/10] rounded-2xl overflow-hidden">
                  <img
                    src="/images/SN-Laptop.svg"
                    alt="Serene Natural Memorial Style"
                    className="w-full h-full object-contain scale-110"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Memorial Section */}
      <div id="custom" className="py-20 md:py-32 relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          {/* Title */}
          <h2 className="text-4xl md:text-5xl font-bold text-white font-montserrat mb-8">
            Looking for Something <span className="bg-gradient-to-r from-primary to-[#E6C45C] text-transparent bg-clip-text">Special?</span>
          </h2>
          
          <p className="text-[#FFFFFFCC] text-lg leading-relaxed mb-16 max-w-3xl">
            While our memorial styles offer beautiful options for most families, we understand that sometimes you want something uniquely personal. We specialize in creating custom memorials that perfectly capture your loved one's story.
          </p>

          {/* Service Items Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 mb-16">
            {/* Left Column */}
            <div>
              {/* Personal Touch */}
              <div className="flex items-center justify-between py-6 border-b border-white/10">
                <div>
                  <h3 className="text-xl font-montserrat font-semibold text-white">
                    Your Vision, Brought to Life
                  </h3>
                  <p className="text-[#FFFFFFCC] text-sm mt-1">A memorial designed exactly how you imagine it</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-[#1A1A1A] flex items-center justify-center text-primary">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              {/* Guidance */}
              <div className="flex items-center justify-between py-6 border-b border-white/10">
                <div>
                  <h3 className="text-xl font-montserrat font-semibold text-white">
                    Personal Developer
                  </h3>
                  <p className="text-[#FFFFFFCC] text-sm mt-1">Who you can contact directly</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-[#1A1A1A] flex items-center justify-center text-primary">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              {/* Special Features */}
              <div className="flex items-center justify-between py-6 border-b border-white/10">
                <div>
                  <h3 className="text-xl font-montserrat font-semibold text-white">
                    Family Tree Features
                  </h3>
                  <p className="text-[#FFFFFFCC] text-sm mt-1">Connect generations of memories together</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-[#1A1A1A] flex items-center justify-center text-primary">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              {/* Media */}
              <div className="flex items-center justify-between py-6 border-b border-white/10">
                <div>
                  <h3 className="text-xl font-montserrat font-semibold text-white">
                    Cherished Memories Media
                  </h3>
                  <p className="text-[#FFFFFFCC] text-sm mt-1">Share photos, videos, and precious moments</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-[#1A1A1A] flex items-center justify-center text-primary">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              {/* Support */}
              <div className="flex items-center justify-between py-6 border-b border-white/10">
                <div>
                  <h3 className="text-xl font-montserrat font-semibold text-white">
                    Lifetime Care & Support
                  </h3>
                  <p className="text-[#FFFFFFCC] text-sm mt-1">We'll always be here to assist you</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-[#1A1A1A] flex items-center justify-center text-primary">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              {/* Updates */}
              <div className="flex items-center justify-between py-6 border-b border-white/10">
                <div>
                  <h3 className="text-xl font-montserrat font-semibold text-white">
                    Simple Future Updates
                  </h3>
                  <p className="text-[#FFFFFFCC] text-sm mt-1">Refresh the app with design updates</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-[#1A1A1A] flex items-center justify-center text-primary">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Description Text and Button */}
          <div className="flex flex-col lg:flex-row items-start justify-between gap-8 lg:gap-16">
            <div className="w-full lg:max-w-3xl">
              <p className="text-[#FFFFFFCC] text-base lg:text-lg leading-relaxed">
                We'll take care of everything for you. From organizing your precious photos and memories to creating special features that your family will cherish. Just share your ideas with us, and we'll make them happen.
              </p>
              <p className="text-[#FFFFFFCC] text-base lg:text-lg leading-relaxed mt-6 lg:mt-8">
                Let's create something beautiful together that perfectly honors your loved one's memory. We're here to listen, guide, and support you every step of the way.
              </p>
            </div>

            <Link
              to="/get-in-touch"
              onClick={() => window.scrollTo(0, 0)}
              className="w-full lg:w-auto inline-flex items-center justify-center gap-2 px-6 lg:px-8 py-4 text-base lg:text-lg font-semibold rounded-lg bg-gradient-to-r from-primary to-[#E6C45C] text-[#0A0A0A] hover:opacity-90 transition-opacity duration-200 font-montserrat whitespace-normal lg:whitespace-nowrap"
            >
              Let's Talk About Your Ideas
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemorialStyles; 